import { Component, OnInit, Input, ViewEncapsulation, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';

import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { CatalogState, Feed, folders, getUserFeeds, userFeeds, getFolders } from '@purplefront/catalog/data-access';
import { BookmarksState, loadBookmarks, selectBookmarksTotal } from '@purplefront/bookmarks/data-access';
import { AppRouterState, selectPage } from '@purplefront/app-router/data-access';

@Component({
  selector: 'app-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav id="nav" class="nav" role="navigation">
      <div
        class="menu-wrapper"
        *ngIf="page$ | async as page"
        [ngClass]="{ 'nav-open': navOpen, 'nav-closed': !navOpen }"
      >
        <app-select-menu
          *ngIf="isDashboardMenu(page)"
          [userFeeds$]="userFeeds$"
          (getFolders$)="getFolders($event)"
          [bookmarkCount]="_bookmarksTotal$ | async"
        >
        </app-select-menu>
        <app-bookmarks-menu *ngIf="isBookmarkMenu(page)" [bookmarkCount]="_bookmarksTotal$ | async">
        </app-bookmarks-menu>
        <app-preferences-menu *ngIf="isPreferencesMenu(page)"> </app-preferences-menu>
      </div>
    </nav>
  `,
  styleUrls: ['./menu.container.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MenuContainer implements OnInit, OnDestroy {
  @Input() navOpen: boolean;

  public userFeeds$: Observable<Feed[]>;
  public bookmarkCount: number;
  public page$: Observable<string>;

  private _bookmarksTotal$: Observable<number>;
  private _ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _bookmarkStore: Store<BookmarksState>,
    private _catalogStore: Store<CatalogState>,
    private _routerStore: Store<AppRouterState>
  ) {}

  ngOnInit() {
    this._catalogStore.dispatch(getUserFeeds());

    /**
     * Get User selected feeds
     */
    this.userFeeds$ = this._catalogStore.pipe(select(userFeeds));

    /**
     * Get current page
     */
    this.page$ = this._routerStore.pipe(select(selectPage));

    /**
     * Get total items saved
     */

    this._bookmarkStore.dispatch(loadBookmarks({ payload: { bookmarkType: 'all' } }));

    this._bookmarksTotal$ = this._bookmarkStore.pipe(select(selectBookmarksTotal));
  }

  /*
   * Get current feed folders
   */

  getFolders(params) {
    this._catalogStore.pipe(debounceTime(0), select(folders), takeUntil(this._ngUnsubscribe$)).subscribe((folder) => {
      if (!folder.length && !folder[params.feed.slug]) {
        this._catalogStore.dispatch(
          getFolders({
            payload: {
              contentLang: params.lang,
              currentFeed: params.feed.slug
            }
          })
        );
      }
    });
  }

  /**
   * Check is dashboard menu
   * @param page
   */
  isDashboardMenu = (page: string) => {
    const pages = ['dashboard', 'radar', 'afprelax', 'reader', 'dataviz', 'calendar', 'folders', 'add-feed'];
    return pages.some((p) => p === page);
  };

  /**
   * Check is preferences menu
   * @param page
   */
  isPreferencesMenu = (page: string) => page === 'preferences';

  /**
   * Check is bookmarks menu
   * @param page
   */
  isBookmarkMenu = (page: string) => page === 'bookmarks';

  /**
   * Prevent memory leaks
   */
  ngOnDestroy(): void {
    this._ngUnsubscribe$.next();
    this._ngUnsubscribe$.complete();
  }
}
